// frontend/src/pages/Institution/EditInstitution.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Building2, Save, Home, X, AlertCircle, Loader2 } from 'lucide-react';
import './Institution.css';

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const EditInstitution = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState({});
  const [institutionTypes, setInstitutionTypes] = useState([]);

  const [data, setData] = useState({
    institution_name: "",
    acronym: "",
    institution_type: "",
    current_manager: "",
    contact: "",
    institution_location: "",
    status: "active",
  });

  const [filePreviews, setFilePreviews] = useState({
    institution_logo: null,
    institution_seal: null,
    institution_signature: null
  });

  const [files, setFiles] = useState({
    institution_logo: null,
    institution_seal: null,
    institution_signature: null
  });

  useEffect(() => {
    fetchInstitution();
    fetchInstitutionTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchInstitution = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${url}/api/institution/${id}`, {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        const inst = response.data.data;
        setData({
          institution_name: inst.institution_name || "",
          acronym: inst.acronym || "",
          institution_type: inst.institution_type || "",
          current_manager: inst.current_manager || "",
          contact: inst.contact || "",
          institution_location: inst.institution_location || "",
          status: inst.status || "active",
        });

        // Set file previews
        const previews = {};
        if (inst.institution_logo) {
          previews.institution_logo = `${url}/uploads/institutions/${inst.institution_logo}`;
        }
        if (inst.institution_seal) {
          previews.institution_seal = `${url}/uploads/institutions/${inst.institution_seal}`;
        }
        if (inst.institution_signature) {
          previews.institution_signature = `${url}/uploads/institutions/${inst.institution_signature}`;
        }
        setFilePreviews(previews);
      }
    } catch (error) {
      toast.error("Failed to load institution data");
      navigate('/ViewInstitution');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstitutionTypes = async () => {
    try {
      const response = await axios.get(`${url}/api/institution/types`, {
        headers: getAuthHeaders()
      });
      if (response.data.success) {
        setInstitutionTypes(response.data.data);
      }
    } catch (err) {
      // silent
    }
  };

  const handleFocus = (field) => setFocused({ ...focused, [field]: true });
  const handleBlur = (field) => setFocused({ ...focused, [field]: data[field] && data[field].length > 0 });

  const handleChange = (e) => {
    const { name, value, files: fileList } = e.target;

    if (fileList) {
      const file = fileList[0];
      setFiles(prev => ({ ...prev, [name]: file }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreviews(prev => ({ ...prev, [name]: reader.result }));
      };
      reader.readAsDataURL(file);
    } else {
      setData(prev => ({ ...prev, [name]: value }));
      // Clear error for this field
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: "" }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!data.institution_name.trim()) {
      newErrors.institution_name = "Institution name is required";
    }

    if (!data.institution_type) {
      newErrors.institution_type = "Institution type is required";
    }

    if (data.contact && !/^[\+]?[1-9][\d]{0,15}$/.test(data.contact)) {
      newErrors.contact = "Invalid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const removeFile = (field) => {
    setFiles(prev => ({ ...prev, [field]: null }));
    setFilePreviews(prev => ({ ...prev, [field]: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      setSubmitting(true);

      const formData = new FormData();

      // Add text fields
      Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== "") {
          formData.append(key, data[key]);
        }
      });

      // Add files if they exist
      Object.keys(files).forEach(key => {
        if (files[key]) {
          formData.append(key, files[key]);
        }
      });

      const response = await axios.put(
        `${url}/api/institution/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            ...getAuthHeaders(),
          },
        }
      );

      if (response.data.success) {
        toast.success("Institution updated successfully!");
        navigate('/ViewInstitution');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Failed to update institution";
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/ViewInstitution');
  };

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="currency-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading institution data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="currency-form-container">
        <button
          className="close-btn"
          onClick={handleCancel}
          title="Close"
        >
          <X size={20} />
        </button>

        <div className="form-header">
          <Building2 size={24} />
          <h2 className="form-title">Edit Institution</h2>
          <p className="form-subtitle">
            Update the institution details below. All fields marked with * are required.
          </p>
        </div>

        <form className="currency-form" onSubmit={handleSubmit} encType="multipart/form-data">
          <div className="form-grid">
            {/* Institution Name */}
            <div className={`form-field required ${focused.institution_name || data.institution_name ? "filled" : ""} ${errors.institution_name ? 'error' : ''}`}>
              <input
                type="text"
                name="institution_name"
                value={data.institution_name}
                onChange={handleChange}
                onFocus={() => handleFocus("institution_name")}
                onBlur={() => handleBlur("institution_name")}
                placeholder="Enter institution name"
                required
              />
              <label>Institution Name *</label>
              {errors.institution_name && <small className="field-error">{errors.institution_name}</small>}
            </div>

            {/* Acronym */}
            <div className={`form-field ${focused.acronym || data.acronym ? "filled" : ""}`}>
              <input
                type="text"
                name="acronym"
                value={data.acronym}
                onChange={handleChange}
                onFocus={() => handleFocus("acronym")}
                onBlur={() => handleBlur("acronym")}
                placeholder="Enter acronym"
              />
              <label>Acronym</label>
            </div>

            {/* Institution Type */}
            <div className={`form-field required ${focused.institution_type || data.institution_type ? "filled" : ""} ${errors.institution_type ? 'error' : ''}`}>
              <select
                name="institution_type"
                value={data.institution_type}
                onChange={handleChange}
                onFocus={() => handleFocus("institution_type")}
                onBlur={() => handleBlur("institution_type")}
                required
              >
                <option value="">Select Institution Type</option>
                {institutionTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
                <option value="ICT">ICT</option>
              </select>
              <label>Institution Type *</label>
              {errors.institution_type && <small className="field-error">{errors.institution_type}</small>}
            </div>

            {/* Current Manager */}
            <div className={`form-field ${focused.current_manager || data.current_manager ? "filled" : ""}`}>
              <input
                type="text"
                name="current_manager"
                value={data.current_manager}
                onChange={handleChange}
                onFocus={() => handleFocus("current_manager")}
                onBlur={() => handleBlur("current_manager")}
                placeholder="Enter current manager"
              />
              <label>Current Manager</label>
            </div>

            {/* Contact */}
            <div className={`form-field ${focused.contact || data.contact ? "filled" : ""} ${errors.contact ? 'error' : ''}`}>
              <input
                type="text"
                name="contact"
                value={data.contact}
                onChange={handleChange}
                onFocus={() => handleFocus("contact")}
                onBlur={() => handleBlur("contact")}
                placeholder="Enter contact number"
              />
              <label>Contact Number</label>
              {errors.contact && <small className="field-error">{errors.contact}</small>}
            </div>

            {/* Institution Location */}
            <div className={`form-field ${focused.institution_location || data.institution_location ? "filled" : ""}`}>
              <input
                type="text"
                name="institution_location"
                value={data.institution_location}
                onChange={handleChange}
                onFocus={() => handleFocus("institution_location")}
                onBlur={() => handleBlur("institution_location")}
                placeholder="Enter institution location"
              />
              <label>Institution Location</label>
            </div>

            {/* Status */}
            <div className={`form-field ${focused.status || data.status ? "filled" : ""}`}>
              <select
                name="status"
                value={data.status}
                onChange={handleChange}
                onFocus={() => handleFocus("status")}
                onBlur={() => handleBlur("status")}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <label>Status</label>
            </div>

            {/* Institution Logo */}
            <div className="form-field file-upload">
              <label>Institution Logo</label>
              <div className="file-upload-area">
                <input
                  type="file"
                  name="institution_logo"
                  accept="image/*"
                  onChange={handleChange}
                  className="file-input"
                />
                <div className="upload-content">
                  {filePreviews.institution_logo ? (
                    <div className="file-preview">
                      <img src={filePreviews.institution_logo} alt="Logo preview" />
                      <button type="button" className="remove-file" onClick={() => removeFile("institution_logo")}>
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <Building2 size={24} />
                      <span>Click to upload new logo</span>
                      <small>Leave empty to keep current</small>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Institution Seal */}
            <div className="form-field file-upload">
              <label>Institution Seal</label>
              <div className="file-upload-area">
                <input
                  type="file"
                  name="institution_seal"
                  accept="image/*"
                  onChange={handleChange}
                  className="file-input"
                />
                <div className="upload-content">
                  {filePreviews.institution_seal ? (
                    <div className="file-preview">
                      <img src={filePreviews.institution_seal} alt="Seal preview" />
                      <button type="button" className="remove-file" onClick={() => removeFile("institution_seal")}>
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <Building2 size={24} />
                      <span>Click to upload new seal</span>
                      <small>Leave empty to keep current</small>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Institution Signature */}
            <div className="form-field file-upload">
              <label>Institution Signature</label>
              <div className="file-upload-area">
                <input
                  type="file"
                  name="institution_signature"
                  accept="image/*"
                  onChange={handleChange}
                  className="file-input"
                />
                <div className="upload-content">
                  {filePreviews.institution_signature ? (
                    <div className="file-preview">
                      <img src={filePreviews.institution_signature} alt="Signature preview" />
                      <button type="button" className="remove-file" onClick={() => removeFile("institution_signature")}>
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <Building2 size={24} />
                      <span>Click to upload new signature</span>
                      <small>Leave empty to keep current</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="form-info">
            <AlertCircle size={14} />
            <small>Only image files (JPG, PNG, GIF, SVG) up to 5MB are allowed.</small>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancel}
              disabled={submitting}
            >
              <Home size={16} className="mr-2" />
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <div className="spinner"></div>
                  Updating...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  Update Institution
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditInstitution;